import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import axiosInstance from "../../axiosInstance";
import { useAuth } from "../../context/AuthContext";
import "./Dashboard.css";

const formatCurrency = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function Dashboard() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [branches, setBranches] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedEmployerId, setSelectedEmployerId] = useState("");
  const [rangeDays, setRangeDays] = useState(30);

  const isSuperAdmin = user?.roleId === 1;

  useEffect(() => {
    if (isSuperAdmin) {
      fetchEmployers();
    } else {
      fetchBranches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSuperAdmin && !selectedEmployerId) {
      return;
    }
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId, selectedEmployerId, rangeDays]);

  const fetchEmployers = async () => {
    try {
      const response = await axiosInstance.get("/employers");
      const list = response?.data || [];
      setEmployers(list);
      if (list.length > 0) {
        setSelectedEmployerId(list[0]?._id || "");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to fetch employers");
    }
  };

  const fetchBranches = async () => {
    try {
      const query = isSuperAdmin && selectedEmployerId ? `?employerId=${selectedEmployerId}` : "";
      const response = await axiosInstance.get(`/branches${query}`);
      const branchList = response?.data?.list || [];
      setBranches(branchList);
      if (branchList.length > 0) {
        setSelectedBranchId(branchList[0]?._id || "");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to fetch branches");
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams();
      if (selectedBranchId) params.append("branchId", selectedBranchId);
      if (isSuperAdmin && selectedEmployerId) params.append("employerId", selectedEmployerId);
      params.append("rangeDays", String(rangeDays));

      const response = await axiosInstance.get(`/transactions/dashboard?${params.toString()}`);
      setDashboard(response?.data?.result || null);

      if (isSuperAdmin && selectedEmployerId) {
        const branchRes = await axiosInstance.get(`/branches?employerId=${selectedEmployerId}`);
        setBranches(branchRes?.data?.list || []);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const trendMax = useMemo(() => {
    const points = dashboard?.trend || [];
    if (!points.length) return 1;
    return Math.max(...points.map((p) => Number(p.sales || 0)), 1);
  }, [dashboard?.trend]);

  return (
    <Box className="dashboard-root">
      <Box className="dashboard-header">
        <Box>
          <Typography variant="h4" className="dashboard-title">
            Business Dashboard
          </Typography>
          <Typography className="dashboard-subtitle">
            Track sales, collections, dues, and operational activity in one place.
          </Typography>
        </Box>

        <Box className="dashboard-filters">
          {isSuperAdmin && (
            <FormControl size="small" className="dashboard-filter-control">
              <InputLabel>Employer</InputLabel>
              <Select
                label="Employer"
                value={selectedEmployerId}
                onChange={(e) => {
                  setSelectedEmployerId(e.target.value);
                  setSelectedBranchId("");
                }}
              >
                {employers.map((emp) => (
                  <MenuItem key={emp._id} value={emp._id}>
                    {emp.employerName || emp.fullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControl size="small" className="dashboard-filter-control">
            <InputLabel>Branch</InputLabel>
            <Select
              label="Branch"
              value={selectedBranchId}
              onChange={(e) => setSelectedBranchId(e.target.value)}
            >
              <MenuItem value="">All Branches</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch._id} value={branch._id}>
                  {branch.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className="dashboard-filter-control">
            <InputLabel>Range</InputLabel>
            <Select
              label="Range"
              value={rangeDays}
              onChange={(e) => setRangeDays(Number(e.target.value))}
            >
              <MenuItem value={7}>Last 7 Days</MenuItem>
              <MenuItem value={30}>Last 30 Days</MenuItem>
              <MenuItem value={90}>Last 90 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Box className="dashboard-loading">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box className="dashboard-kpi-grid">
            <Paper className="dashboard-kpi-card">
              <Typography className="kpi-label">Total Sales</Typography>
              <Typography className="kpi-value">
                {formatCurrency(dashboard?.overview?.totalSales)}
              </Typography>
            </Paper>
            <Paper className="dashboard-kpi-card">
              <Typography className="kpi-label">Collected (Cash + Tray Credit)</Typography>
              <Typography className="kpi-value">
                {formatCurrency(
                  Number(dashboard?.overview?.totalPayments || 0) +
                    Number(dashboard?.overview?.trayCreditAmount || 0)
                )}
              </Typography>
            </Paper>
            <Paper className="dashboard-kpi-card">
              <Typography className="kpi-label">Total Due</Typography>
              <Typography className="kpi-value">{formatCurrency(dashboard?.overview?.totalDueAmount)}</Typography>
            </Paper>
            <Paper className="dashboard-kpi-card">
              <Typography className="kpi-label">Today Sales</Typography>
              <Typography className="kpi-value">{formatCurrency(dashboard?.overview?.todaySales)}</Typography>
            </Paper>
            <Paper className="dashboard-kpi-card">
              <Typography className="kpi-label">Active Customers</Typography>
              <Typography className="kpi-value">{dashboard?.overview?.totalCustomers || 0}</Typography>
            </Paper>
            <Paper className="dashboard-kpi-card">
              <Typography className="kpi-label">Transactions ({rangeDays}d)</Typography>
              <Typography className="kpi-value">{dashboard?.overview?.totalTransactions || 0}</Typography>
            </Paper>
          </Box>

          <Box className="dashboard-content-grid">
            <Paper className="dashboard-panel dashboard-trend-panel">
              <Typography variant="h6" className="panel-title">
                Sales Trend
              </Typography>
              <Box className="trend-bars">
                {(dashboard?.trend || []).map((point) => {
                  const height = (Number(point.sales || 0) / trendMax) * 100;
                  return (
                    <Box key={point.date} className="trend-item" title={`${point.date} - ${formatCurrency(point.sales)}`}>
                      <Box className="trend-bar-wrapper">
                        <Box className="trend-bar" style={{ height: `${Math.max(height, 3)}%` }} />
                      </Box>
                      <Typography className="trend-label">{point.date.slice(5)}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>

            <Paper className="dashboard-panel">
              <Typography variant="h6" className="panel-title">
                Top Due Customers
              </Typography>
              <Box className="list-scroll">
                {(dashboard?.topDueCustomers || []).map((item) => (
                  <Box key={item.customerId} className="list-row">
                    <Typography className="list-primary">{item.name}</Typography>
                    <Typography className="list-value">{formatCurrency(item.dueAmount)}</Typography>
                  </Box>
                ))}
                {!dashboard?.topDueCustomers?.length && (
                  <Typography className="empty-copy">No due customers in selected range.</Typography>
                )}
              </Box>
            </Paper>

            <Paper className="dashboard-panel">
              <Typography variant="h6" className="panel-title">
                Branch Performance
              </Typography>
              <Box className="list-scroll">
                {(dashboard?.branchPerformance || []).map((item) => (
                  <Box key={item.branchName} className="branch-card">
                    <Typography className="list-primary">{item.branchName}</Typography>
                    <Typography className="branch-meta">
                      Customers: {item.customers} | Business: {formatCurrency(item.totalBusiness)}
                    </Typography>
                    <Typography className="branch-due">Due: {formatCurrency(item.dueAmount)}</Typography>
                  </Box>
                ))}
                {!dashboard?.branchPerformance?.length && (
                  <Typography className="empty-copy">No branch data found.</Typography>
                )}
              </Box>
            </Paper>

            <Paper className="dashboard-panel">
              <Typography variant="h6" className="panel-title">
                Recent Transactions
              </Typography>
              <Box className="list-scroll">
                {(dashboard?.recentTransactions || []).map((item, index) => (
                  <Box key={`${item.customerId}-${index}`} className="recent-row">
                    <Box>
                      <Typography className="list-primary">{item.customerName}</Typography>
                      <Typography className="recent-date">{formatDate(item.date)}</Typography>
                    </Box>
                    <Box className="recent-amounts">
                      <Typography className="recent-sales">Sale: {formatCurrency(item.sales)}</Typography>
                      <Typography className="recent-payment">Paid: {formatCurrency(item.payment)}</Typography>
                    </Box>
                  </Box>
                ))}
                {!dashboard?.recentTransactions?.length && (
                  <Typography className="empty-copy">No recent transactions available.</Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </>
      )}
    </Box>
  );
}

export default Dashboard;
